import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Users, FileText, Clock, TrendingUp, Package, Plus,
  ArrowRight, ChevronRight, BarChart2, Tag, AlertCircle,
  Zap, CheckCircle2, Truck
} from 'lucide-react';
import { toast } from 'sonner';

const IndianRupeeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h12M6 8h12M6 13l8.5-8M6 13h5.5a4 4 0 110 8H6" />
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  picked:    { label: 'Picked Up',  cls: 'bg-orange-100 text-orange-700' },
  washed:    { label: 'Processing', cls: 'bg-blue-100 text-blue-700' },
  ready:     { label: 'Ready',      cls: 'bg-violet-100 text-violet-700' },
  delivered: { label: 'Delivered',  cls: 'bg-emerald-100 text-emerald-700' },
};

// ── Animated counter ──────────────────────────────────────────────────────
const useCountUp = (target, start) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!start || !target) return;
    const n = parseFloat(String(target).replace(/[^0-9.]/g, '')) || 0;
    const t0 = performance.now();
    const dur = 900;
    const tick = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setV(e * n);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [target, start]);
  return v;
};

// ── KPI Card ──────────────────────────────────────────────────────────────
const KpiCard = ({ title, rawValue, displayValue, sub, subIcon: SubIcon, subColor, iconBg, iconColor, Icon, border }) => {
  const [started, setStarted] = useState(false);
  const count = useCountUp(rawValue, started);

  useEffect(() => {
    const t = setTimeout(() => setStarted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const formatted = displayValue
    ? displayValue.replace(/[\d.]+/, () => {
        const n = parseFloat(displayValue.replace(/[^0-9.]/g, ''));
        return Number.isInteger(n) ? Math.round(count).toLocaleString('en-IN') : count.toFixed(1);
      })
    : Math.round(count).toLocaleString('en-IN');

  return (
    <div className={`bg-white rounded-2xl p-6 border-2 ${border} shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-default`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${iconBg}`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
        <span className={`text-xs font-bold px-2 py-1 rounded-full ${subColor} opacity-80`}>Live</span>
      </div>
      <p className="text-sm font-semibold text-slate-500 mb-1">{title}</p>
      <h3 className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{formatted}</h3>
      {sub && (
        <p className={`text-xs font-semibold mt-2 flex items-center gap-1 ${subColor}`}>
          {SubIcon && <SubIcon className="w-3 h-3" />}{sub}
        </p>
      )}
    </div>
  );
};

// ── Progress bar ──────────────────────────────────────────────────────────
const ProgressBar = ({ label, value, max, colorClass, bgClass }) => {
  const [width, setWidth] = useState(0);
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  useEffect(() => { const t = setTimeout(() => setWidth(pct), 200); return () => clearTimeout(t); }, [pct]);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-600">{label}</span>
        <div className="flex items-center gap-1.5">
          <span className={`text-sm font-black ${colorClass}`}>{value}</span>
          <span className="text-xs text-slate-400">/ {max}</span>
        </div>
      </div>
      <div className={`w-full h-2.5 rounded-full overflow-hidden ${bgClass}`}>
        <div className={`h-full rounded-full transition-all duration-1000 ease-out ${colorClass.replace('text-', 'bg-')}`}
          style={{ width: `${width}%` }} />
      </div>
    </div>
  );
};

// ── Quick Action button ───────────────────────────────────────────────────
const QuickAction = ({ label, icon: Icon, path, primary, navigate, testid }) => (
  primary
    ? <button data-testid={testid} onClick={() => navigate(path)}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-2xl flex items-center justify-center gap-2 text-sm transition-all hover:shadow-xl hover:shadow-blue-200 hover:-translate-y-0.5 active:scale-95">
        <Plus className="w-4 h-4" />{label}
      </button>
    : <button data-testid={testid} onClick={() => navigate(path)}
        className="w-full bg-white border-2 border-slate-100 hover:border-blue-200 hover:bg-blue-50 text-slate-700 font-semibold py-2.5 px-4 rounded-2xl flex items-center justify-between text-sm transition-all hover:shadow-sm active:scale-95 group">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
            <Icon className="w-3.5 h-3.5 text-blue-600" />
          </div>
          {label}
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
      </button>
);

// ── Dashboard ─────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [sRes, tRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tickets`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(sRes.data);
      setRecentTickets(tRes.data.slice(0, 8));
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        <p className="text-slate-400 text-sm font-medium">Loading dashboard...</p>
      </div>
    );
  }

  const kpiCards = [
    {
      title: 'Total Customers',
      rawValue: stats?.total_customers || 0,
      displayValue: String(stats?.total_customers || 0),
      sub: 'Active this cycle',
      subIcon: TrendingUp,
      subColor: 'text-blue-600',
      Icon: Users,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      border: 'border-blue-100',
    },
    {
      title: 'Active Tickets',
      rawValue: stats?.active_tickets || 0,
      displayValue: String(stats?.active_tickets || 0),
      sub: `${stats?.total_tickets || 0} total tickets`,
      subIcon: AlertCircle,
      subColor: 'text-orange-500',
      Icon: FileText,
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600',
      border: 'border-orange-100',
    },
    {
      title: 'Cycle Revenue',
      rawValue: stats?.current_cycle_revenue || 0,
      displayValue: `₹${(stats?.current_cycle_revenue || 0).toFixed(0)}`,
      sub: `Pending ₹${(stats?.pending_amount || 0).toFixed(0)}`,
      subIcon: TrendingUp,
      subColor: 'text-emerald-600',
      Icon: IndianRupeeIcon,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      border: 'border-emerald-100',
    },
    {
      title: 'KG Processed',
      rawValue: stats?.total_kg_processed || 0,
      displayValue: `${(stats?.total_kg_processed || 0).toFixed(1)} kg`,
      sub: `Today: ${stats?.today_pickups || 0} pickups`,
      subIcon: Package,
      subColor: 'text-violet-600',
      Icon: Package,
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-600',
      border: 'border-violet-100',
    },
  ];

  const quickActions = [
    { label: 'Create New Ticket', path: '/dashboard/tickets/create', primary: true,  icon: Plus,         testid: 'quick-create-ticket-btn' },
    { label: 'View Customers',    path: '/dashboard/customers',       primary: false, icon: Users,        testid: 'quick-view-customers-btn' },
    { label: 'Manage Rates',      path: '/dashboard/rates',           primary: false, icon: Tag,          testid: 'quick-manage-rates-btn' },
    { label: 'Billing History',   path: '/dashboard/billing',         primary: false, icon: FileText,     testid: 'quick-view-billing-btn' },
    { label: 'Analytics',         path: '/dashboard/analytics',       primary: false, icon: BarChart2,    testid: 'quick-analytics-btn' },
  ];

  return (
    <div className="space-y-6">

      {/* ── Welcome banner ── */}
      <div className="bg-gradient-to-r from-blue-600 to-violet-600 rounded-3xl p-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-blue-100 text-sm font-medium mb-1">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
            <h2 className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0] || 'Admin'} 👋
            </h2>
            <p className="text-blue-100 text-sm mt-1">Here's what's happening with your laundry operations today.</p>
          </div>
          <button onClick={() => navigate('/dashboard/tickets/create')}
            className="bg-white text-blue-600 font-bold px-5 py-2.5 rounded-2xl text-sm flex items-center gap-2 hover:shadow-lg hover:-translate-y-0.5 transition-all flex-shrink-0">
            <Plus className="w-4 h-4" /> New Ticket
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpiCards.map(card => <KpiCard key={card.title} {...card} />)}
      </div>

      {/* ── Main grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Left: Quick actions + ops status */}
        <div className="space-y-5">

          {/* Quick actions */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center">
                <Zap className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Quick Actions</h2>
            </div>
            <div className="space-y-2">
              {quickActions.map(a => <QuickAction key={a.label} {...a} navigate={navigate} />)}
            </div>
          </div>

          {/* Operations status */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 bg-emerald-50 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
              <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Operations Today</h2>
            </div>
            <div className="space-y-5">
              <ProgressBar
                label="Pickups Completed"
                value={stats?.today_pickups || 0}
                max={Math.max(stats?.today_pickups || 0, 10)}
                colorClass="text-blue-600"
                bgClass="bg-blue-50"
              />
              <ProgressBar
                label="Deliveries Done"
                value={stats?.today_deliveries || 0}
                max={Math.max(stats?.today_deliveries || 0, 10)}
                colorClass="text-emerald-600"
                bgClass="bg-emerald-50"
              />
              <ProgressBar
                label="Active Tickets"
                value={stats?.active_tickets || 0}
                max={Math.max(stats?.total_tickets || 1, 1)}
                colorClass="text-orange-500"
                bgClass="bg-orange-50"
              />
            </div>

            {/* Mini stat row */}
            <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
              {[
                { label: 'Total Tickets', val: stats?.total_tickets || 0, color: 'text-blue-600', bg: 'bg-blue-50' },
                { label: 'Pending ₹',     val: `₹${(stats?.pending_amount||0).toFixed(0)}`, color: 'text-orange-600', bg: 'bg-orange-50' },
              ].map(s => (
                <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
                  <p className={`text-lg font-black ${s.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{s.val}</p>
                  <p className="text-xs text-slate-500 font-medium mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Recent tickets table */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-violet-50 rounded-xl flex items-center justify-center">
                <FileText className="w-4 h-4 text-violet-600" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Tickets</h2>
                <p className="text-[11px] text-slate-400">Latest activity</p>
              </div>
            </div>
            <button onClick={() => navigate('/dashboard/tickets')} data-testid="view-all-tickets-btn"
              className="text-blue-600 hover:text-blue-700 text-sm font-bold flex items-center gap-1 hover:gap-2 transition-all">
              View All <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {recentTickets.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-16 text-slate-400 gap-3">
              <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center">
                <FileText className="w-8 h-8 opacity-40" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-600">No tickets yet</p>
                <p className="text-xs mt-1">Create your first ticket to get started</p>
              </div>
              <button onClick={() => navigate('/dashboard/tickets/create')}
                className="mt-2 bg-blue-600 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors">
                Create Ticket
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto flex-1">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50">
                      {['Ticket ID', 'Customer', 'Service', 'Amount', 'Status'].map(h => (
                        <th key={h} className="px-5 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentTickets.map((ticket, i) => {
                      const status = STATUS_CONFIG[ticket.status] || { label: ticket.status, cls: 'bg-slate-100 text-slate-600' };
                      return (
                        <tr key={ticket.id}
                          onClick={() => navigate(`/dashboard/tickets/${ticket.id}`)}
                          data-testid={`recent-ticket-${ticket.id}`}
                          className={`cursor-pointer hover:bg-blue-50/60 transition-colors border-t border-slate-50 ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}>
                          <td className="px-5 py-3.5">
                            <span className="font-mono text-xs font-black text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">{ticket.ticket_number}</span>
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="text-sm font-semibold text-slate-800 whitespace-nowrap">{ticket.customer_name}</p>
                            <p className="text-[11px] text-slate-400">{ticket.kg} kg</p>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="text-sm text-slate-600 capitalize whitespace-nowrap">{ticket.service_type || 'Wash & Fold'}</span>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="text-sm font-black text-slate-900">₹{ticket.total_price.toFixed(0)}</span>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide whitespace-nowrap ${status.cls}`}>
                              {status.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 border-t border-slate-100">
                <button onClick={() => navigate('/dashboard/tickets')}
                  className="w-full text-slate-400 hover:text-blue-600 text-sm font-semibold flex items-center justify-center gap-1 py-1 transition-colors">
                  Show all tickets <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
